import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle, Clock } from "lucide-react";
import { BrandLogo } from "@/components/Brand";

const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | failed | error
  const [info, setInfo] = useState(null);

  const poll = useCallback(async (attempt) => {
    if (!sessionId) { setState("error"); return; }
    try {
      const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
      setInfo(data);
      if (data.payment_status === "paid") { setState("paid"); return; }
      if (data.status === "expired" || data.payment_status === "failed") { setState("failed"); return; }
      if (attempt >= MAX_POLLS) { setState("pending"); return; }
      setTimeout(() => poll(attempt + 1), 2000);
    } catch (e) {
      if (attempt >= MAX_POLLS) { setState("error"); return; }
      setTimeout(() => poll(attempt + 1), 2000);
    }
  }, [sessionId]);

  useEffect(() => { poll(0); }, [poll]);

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-6">
      <div className="bg-white border border-border rounded-3xl p-10 max-w-md w-full text-center animate-fade-up">
        <div className="flex justify-center mb-6">
          <BrandLogo className="h-11" />
        </div>

        {state === "checking" && (
          <>
            <Loader2 className="w-14 h-14 text-gold animate-spin mx-auto mb-5" />
            <h1 className="font-heading text-2xl font-bold text-ink mb-2">Confirmando pagamento…</h1>
            <p className="text-cappuccino">Isso leva só alguns segundos.</p>
          </>
        )}

        {state === "paid" && (
          <>
            <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto mb-5" />
            <h1 className="font-heading text-2xl font-bold text-ink mb-2" data-testid="payment-success">Assinatura confirmada! 🎉</h1>
            <p className="text-cappuccino mb-6">Bem-vindo(a) ao Seu Chamador. Sua assinatura está ativa.</p>
            <Link to="/login"><Button className="rounded-full bg-gold hover:bg-gold-hover text-white h-12 px-8 w-full">Acessar o painel</Button></Link>
          </>
        )}

        {state === "pending" && (
          <>
            <Clock className="w-14 h-14 text-cappuccino mx-auto mb-5" />
            <h1 className="font-heading text-2xl font-bold text-ink mb-2">Pagamento em processamento</h1>
            <p className="text-cappuccino mb-6">Assim que for confirmado, sua assinatura é ativada. Você pode acessar o painel normalmente.</p>
            <Link to="/login"><Button variant="outline" className="rounded-full h-12 px-8 w-full">Ir para o painel</Button></Link>
          </>
        )}

        {(state === "failed" || state === "error") && (
          <>
            <XCircle className="w-14 h-14 text-destructive mx-auto mb-5" />
            <h1 className="font-heading text-2xl font-bold text-ink mb-2">Não foi possível confirmar</h1>
            <p className="text-cappuccino mb-6">O pagamento não foi concluído. Tente novamente ou escolha outro plano.</p>
            <Link to="/planos"><Button className="rounded-full bg-gold hover:bg-gold-hover text-white h-12 px-8 w-full">Ver planos</Button></Link>
          </>
        )}
      </div>
    </div>
  );
}
